import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './UpdatePharmacyProfile.css';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../Sidebar/Sidebar';
import api from '../../api';
import { cloudApi } from '../../api';

const ENV = process.env.REACT_APP_ENV || "production";
const IS_DESKTOP = ENV === "desktop";

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

// ── Network error helper ──
const getNetworkErrorMessage = (err, fallback = "Something went wrong. Please try again.") => {
  const isNetworkError = (
    !navigator.onLine ||
    err.code === 'ERR_NETWORK' ||
    err.code === 'ERR_INTERNET_DISCONNECTED' ||
    err.code === 'ERR_NAME_NOT_RESOLVED' ||
    err.code === 'ERR_CONNECTION_REFUSED' ||
    err.code === 'ERR_CONNECTION_TIMED_OUT' ||
    err.message === 'Network Error' ||
    err.message?.includes('ERR_INTERNET_DISCONNECTED') ||
    err.message?.includes('net::')
  );
  if (isNetworkError) {
    return '📡 No internet connection. Please check your network and try again.';
  }
  if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
    return '⏱ Request timed out. The server took too long to respond. Please try again.';
  }
  if (err.response) {
    const status = err.response.status;
    const detail = err.response.data?.detail;
    if (status === 401) return '🔒 Session expired. Please log in again.';
    if (status === 403) return `🚫 Access denied. ${detail || "You don't have permission for this action."}`;
    if (status === 404) return `🔍 Not found. ${detail || 'The requested resource was not found.'}`;
    if (status === 413) return '❌ Your image is too large. Maximum allowed size is 1MB. Please choose a smaller image.';
    if (status === 422) return `❌ Invalid data. ${detail || 'Please check your input and try again.'}`;
    if (status >= 500) return `🔧 Server error (${status}). Please try again later or contact support.`;
    if (detail) return detail;
  }
  return fallback;
};

const UpdatePharmacyProfile = () => {
  const [formData, setFormData] = useState({
    pharmacy_address: '',
    city: '',
    state: '',
    country: '',
    latitude: '',
    longitude: '',
    logo: '',
  });

  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [logoError, setLogoError] = useState('');
  const [logoSuccess, setLogoSuccess] = useState('');

  // ── Confirm dialog state ──
  const [confirmDialog, setConfirmDialog] = useState({
    visible: false,
    message: '',
    onConfirm: null,
  });

  // ── Online status ──
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const navigate = useNavigate();

  // ── Confirm dialog helper ──
  const showConfirm = (message) => {
    return new Promise((resolve) => {
      setConfirmDialog({
        visible: true,
        message,
        onConfirm: (result) => {
          setConfirmDialog({ visible: false, message: '', onConfirm: null });
          resolve(result);
        },
      });
    });
  };

  // ── Online/offline listener ──
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Fetch profile on mount ──
  useEffect(() => {
    const fetchProfile = async () => {
      setIsLoading(true);

      if (!navigator.onLine) {
        setError('No internet connection. Please connect and try again.');
        setIsLoading(false);
        return;
      }

      try {
        const res = await cloudApi.get('/users/profile');
        const data = res.data.profile;
        setFormData({
          pharmacy_address: data.pharmacy_address || '',
          city: data.city || '',
          state: data.state || '',
          country: 'Nigeria',
          latitude: data.latitude || '',
          longitude: data.longitude || '',
          logo: data.logo || '',
        });
      } catch (err) {
        setError(getNetworkErrorMessage(err, 'Failed to load profile.'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, []);

  // ── Auto-geocoding ──
  useEffect(() => {
    const timeout = setTimeout(async () => {
      const { pharmacy_address, city, state, country } = formData;
      if (pharmacy_address && city && state && country) {
        const fullAddress = `${pharmacy_address}, ${city}, ${state}, ${country}`;
        try {
          const geo = await axios.get(`https://maps.googleapis.com/maps/api/geocode/json`, {
            params: { address: fullAddress, key: GOOGLE_API_KEY },
          });
          const results = geo?.data?.results;
          if (results && results.length > 0) {
            const loc = results[0].geometry.location;
            setFormData(prev => ({
              ...prev,
              latitude: loc.lat.toFixed(6),
              longitude: loc.lng.toFixed(6),
            }));
          }
        } catch {
          // Geocoding failure is non-fatal — user can still submit manually
        }
      }
    }, 800);
    return () => clearTimeout(timeout);
  }, [formData.pharmacy_address, formData.city, formData.state, formData.country, formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ── Submit profile update ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!isOnline) {
      setError('No internet connection. Please connect and try again.');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        pharmacy_address: formData.pharmacy_address,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        latitude: parseFloat(formData.latitude) || 0,
        longitude: parseFloat(formData.longitude) || 0,
      };

      // ── Step 1: Save to cloud ──
      await cloudApi.put('/users/pharmacy/profile', payload);
      setLoading(false);

      // ── Step 2: Full sync so local SQLite reflects the change immediately ──
      if (navigator.onLine) {
        try {
          setSyncing(true);
          if (IS_DESKTOP) {
            await api.post('/sync/now');
          } else {
            await api.post('/sync/pull');
          }
        } catch {
          // Non-fatal — background sync will catch it within 2 minutes
        } finally {
          setSyncing(false);
        }
      }

      setSuccess('Profile updated successfully! Redirecting...');
      setTimeout(() => navigate('/PharmacyProfile'), 1500);

    } catch (err) {
      setLoading(false);
      setError(getNetworkErrorMessage(err, 'Failed to update profile.'));
    }
  };

  // ── Logo upload ──
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLogoError('');
    setLogoSuccess('');

    if (!isOnline) {
      setLogoError('No internet connection. Logo upload requires an internet connection.');
      return;
    }

    // ── Check file size before uploading (max 1MB) ──
    const MAX_SIZE_MB = 1;
    const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
    if (file.size > MAX_SIZE_BYTES) {
      setLogoError(`❌ Your image is too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum allowed size is 1MB. Please choose a smaller image.`);
      e.target.value = '';
      return;
    }

    const formDataUpload = new FormData();
    formDataUpload.append('logo', file);

    try {
      const res = await cloudApi.put('/users/pharmacy/logo', formDataUpload);
      setFormData(prev => ({ ...prev, logo: res?.data?.logo || prev.logo }));
      setLogoSuccess('✅ Logo updated successfully!');
    } catch (err) {
      if (!err.response) {
        setLogoError('❌ Your image is too large. Maximum allowed size is 1MB. Please choose a smaller image.');
      } else {
        setLogoError(getNetworkErrorMessage(err, 'Failed to upload profile image.'));
      }
    }
  };

  // ── Full-page loading screen ──
  if (isLoading) {
    return (
      <div className="upd-profile-page">
        {!isMobile && <aside className="upd-sidebar"><Sidebar /></aside>}
        <main className="upd-main-content">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading profile data...</p>
          </div>
        </main>
      </div>
    );
  }

  // ── Offline screen ──
  if (!isOnline && !formData.pharmacy_address) {
    return (
      <div className="upd-profile-page">
        {!isMobile && <aside className="upd-sidebar"><Sidebar /></aside>}
        <main className="upd-main-content">
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', minHeight: '60vh', gap: '1rem', textAlign: 'center',
          }}>
            <div style={{ fontSize: '3rem' }}>📡</div>
            <h3 style={{ color: '#333', margin: 0 }}>No Internet Connection</h3>
            <p style={{ color: '#666', maxWidth: '360px', lineHeight: 1.6, margin: 0 }}>
              Updating your pharmacy profile requires an internet connection.
              Please check your network and try again.
            </p>
            <button
              onClick={() => {
                if (navigator.onLine) { setIsOnline(true); window.location.reload(); }
                else setError('Still no internet connection.');
              }}
              style={{
                padding: '0.7rem 1.75rem', background: '#007bff', color: 'white',
                border: 'none', borderRadius: '6px', fontSize: '0.95rem',
                cursor: 'pointer', fontWeight: 500,
              }}
            >
              🔄 Retry
            </button>
            {error && <p style={{ color: '#dc3545', fontSize: '0.85rem', margin: 0 }}>{error}</p>}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="upd-profile-page">
      {!isMobile && <aside className="upd-sidebar"><Sidebar /></aside>}

      {/* ── React Confirm Dialog ── */}
      {confirmDialog.visible && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999,
        }}>
          <div style={{
            background: '#fff', borderRadius: '10px', padding: '2rem',
            maxWidth: '420px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          }}>
            <p style={{ color: '#333', fontSize: '1rem', marginBottom: '1.5rem', lineHeight: 1.6 }}>
              {confirmDialog.message}
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => confirmDialog.onConfirm(false)}
                style={{
                  padding: '0.6rem 1.25rem', background: '#f8f9fa', color: '#333',
                  border: '1px solid #ddd', borderRadius: '6px', cursor: 'pointer',
                  fontWeight: 500, fontSize: '0.95rem',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => confirmDialog.onConfirm(true)}
                style={{
                  padding: '0.6rem 1.25rem', background: '#007bff', color: 'white',
                  border: 'none', borderRadius: '6px', cursor: 'pointer',
                  fontWeight: 600, fontSize: '0.95rem',
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      <main className="upd-main-content">
        <div className="upd-form-wrapper">
          <h2>Update Pharmacy Profile</h2>

          {/* ── Offline banner ── */}
          {!isOnline && (
            <div style={{
              background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px',
              padding: '0.75rem 1rem', marginBottom: '1rem',
              display: 'flex', alignItems: 'center', gap: '0.65rem', fontSize: '0.88rem',
            }}>
              <span style={{ fontSize: '1.2rem' }}>📡</span>
              <div>
                <strong style={{ color: '#856404' }}>You are offline</strong>
                <p style={{ margin: 0, color: '#856404', fontSize: '0.82rem' }}>
                  You can view your current data but saving changes requires an internet connection.
                </p>
              </div>
            </div>
          )}

          {/* ── Syncing banner ── */}
          {syncing && (
            <div style={{
              background: '#e7f3ff', border: '1px solid #007bff', borderRadius: '8px',
              padding: '0.75rem 1rem', marginBottom: '1rem',
              display: 'flex', alignItems: 'center', gap: '0.65rem', fontSize: '0.88rem',
            }}>
              <span style={{ fontSize: '1.2rem' }}>🔄</span>
              <div>
                <strong style={{ color: '#0056b3' }}>Syncing changes...</strong>
                <p style={{ margin: 0, color: '#0056b3', fontSize: '0.82rem' }}>
                  Pulling updated profile to local storage. Please wait.
                </p>
              </div>
            </div>
          )}

          {/* ── Logo section ── */}
          <div className="upd-logo-section">
            <label htmlFor="profile-image-upload" className="upd-logo-label">
              📷 Update Profile Image:
            </label>
            {formData.logo && (
              <img src={formData.logo} alt="Logo" className="upd-logo-preview" />
            )}
            <input
              type="file"
              id="profile-image-upload"
              accept="image/*"
              onChange={handleLogoUpload}
              className="upd-logo-input"
              disabled={!isOnline}
              title={!isOnline ? 'Internet connection required to upload logo' : undefined}
              style={!isOnline ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
            />
            <p style={{ color: '#666', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>
              Maximum file size: 1MB. Supported formats: JPG, PNG, GIF, WebP
            </p>
            {!isOnline && (
              <p style={{ color: '#856404', fontSize: '0.8rem', margin: '0.25rem 0 0' }}>
                Logo upload requires internet
              </p>
            )}
            {/* ── Logo error/success shown right next to the image field ── */}
            {logoError && (
              <p style={{
                color: '#dc3545', fontSize: '0.85rem', margin: '0.5rem 0 0',
                background: '#fff5f5', border: '1px solid #dc3545',
                borderRadius: '6px', padding: '0.5rem 0.75rem',
              }}>
                {logoError}
              </p>
            )}
            {logoSuccess && (
              <p style={{
                color: '#28a745', fontSize: '0.85rem', margin: '0.5rem 0 0',
                background: '#f0fff4', border: '1px solid #28a745',
                borderRadius: '6px', padding: '0.5rem 0.75rem',
              }}>
                {logoSuccess}
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="upd-form">
            <label>Pharmacy Address:</label>
            <input
              type="text"
              name="pharmacy_address"
              value={formData.pharmacy_address}
              onChange={handleChange}
              required
            />

            <label>City:</label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              required
            />

            <label>State:</label>
            <input
              type="text"
              name="state"
              value={formData.state}
              onChange={handleChange}
              required
            />

            <label>Country:</label>
            <input
              type="text"
              name="country"
              value={formData.country}
              onChange={handleChange}
              required
            />

            <button
              type="submit"
              disabled={loading || syncing || !isOnline}
              title={!isOnline ? 'Internet connection required to save changes' : undefined}
              style={loading || syncing || !isOnline ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
            >
              {loading ? 'Saving...' : syncing ? 'Syncing...' : 'Update Profile'}
            </button>

            {error && <p className="upd-error">{error}</p>}
            {success && <p className="upd-success">{success}</p>}
          </form>
        </div>
      </main>
    </div>
  );
};

export default UpdatePharmacyProfile;